import React, { useState, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "../css/CollectionReport.css";
import { useNavigate } from "react-router-dom";
import { Download, FileTextIcon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeeReport } from "../redux/slices/employeeReportSlice";
import { toast } from "react-toastify";

const API_URL = "https://payyug.in/api";

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100, "All"];

const CollectionReport = () => {
  const { list: employeeReport } = useSelector((state) => state.employeeReport);
  const dispatch = useDispatch();
  const [collections, setCollections] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [company, setCompany] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loanType, setLoanType] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const navigate = useNavigate();
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

  useEffect(() => {
    dispatch(fetchEmployeeReport({ page: currentPage, superAdminId }));
  }, [dispatch, currentPage, superAdminId]);

  const employeesList = employeeReport?.data || employeeReport || [];

  // 🔹 Fetch data from API
  useEffect(() => {
    const fetchCollections = async () => {
      const params = {
        superAdminId,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      };
      if (selectedEmployee) {
        params.branchId = selectedEmployee?.id;
      }
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/loan-emi/collection-sheet`, {
          params,
        });
        if (res.data && res.data.collections) {
          setCollections(res.data.collections);
        }
      } catch (error) {
        console.error("Error fetching collection report:", error);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) {
      fetchCollections();
    }
  }, [superAdminId, selectedEmployee, fromDate, toDate]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`,
        );
        const data = await response.json();

        if (response.ok && data) {
          // Dynamically construct logo URL
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        } else {
          console.error("Failed to fetch company data");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [navigate, superAdminId]);

  const handleOpenVerifyModal = (item) => {
    setSelectedCollection(item);
    setPaymentMode("CASH");
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setVerifyModalOpen(true);
  };

  // API handler matching your payload structure
  const handleConfirmVerify = async () => {
    if (!selectedCollection) return;

    if (!paymentDate) {
      alert("Please select a payment date.");
      return;
    }

    try {
      setVerifying(true);
      const res = await axios.post(
        `${API_URL}/loan/pay-emi/withpanalty?loanId=${selectedCollection?.loanId}&emiNumber=${selectedCollection?.emiNumber}`,
        {
          emiNumber: selectedCollection.id,
          superAdminId,
          amountPaid: selectedCollection?.emiAmount,
          paymentMode,
          paymentDate,
        },
      );

      if (res.data?.success || res.status === 200) {
        toast.success(res?.data?.message||"Payment verified.")
        setCollections((prev) =>
          prev.map((c) =>
            c.id === selectedCollection.id
              ? {
                  ...c,
                  status: "paid",
                  paymentDate: new Date(paymentDate).toISOString(),
                }
              : c,
          ),
        );
        setVerifyModalOpen(false);
        setSelectedCollection(null);
      }
    } catch (error) {
      console.error("Error verifying collection:", error);
      alert(error?.response?.data?.message || "Failed to verify collection.");
    } finally {
      setVerifying(false);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const val = e.target.value;
    setItemsPerPage(val === "All" ? "All" : Number(val));
    setCurrentPage(1);
  };

  // 🔹 Pagination

  const filteredCollections = collections.filter((c) => {
    const matchesStatus =
      statusFilter === "all" ||
      c.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const isShowAll = itemsPerPage === "All";
  const effectiveItemsPerPage = isShowAll
    ? filteredCollections.length
    : Number(itemsPerPage);

  const totalPages =
    isShowAll || filteredCollections.length === 0
      ? 1
      : Math.ceil(filteredCollections.length / effectiveItemsPerPage);

  const startIndex = isShowAll ? 0 : (currentPage - 1) * effectiveItemsPerPage;

  const currentData = isShowAll
    ? filteredCollections
    : filteredCollections.slice(startIndex, startIndex + effectiveItemsPerPage);

  const handlePrev = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNext = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);

  const handleDownloadPDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // ================= LOGO =================
    const logoX = 15;
    const logoY = 12;
    const logoSize = 25;
    const logoPadding = 2;

    // Border color & thickness
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);

    // Calculate center of logo
    const centerX = logoX + logoSize / 2;
    const centerY = logoY + logoSize / 2;

    // Radius = half of logo + padding
    const radius = logoSize / 2 + logoPadding;

    // Draw circle
    doc.circle(centerX, centerY, radius);

    // Logo image
    if (company?.logoUrl) {
      try {
        const logoResp = await fetch(company.logoUrl);
        const logoBlob = await logoResp.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = () => {
            doc.addImage(
              reader.result,
              "PNG",
              logoX,
              logoY,
              logoSize,
              logoSize,
            );
            resolve();
          };
          reader.readAsDataURL(logoBlob);
        });
      } catch {
        // silent fail
      }
    }

    // ================= COMPANY NAME (AUTO WRAP) =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);

    const nameX = logoX + logoSize + 5;
    const nameY = 18;
    const nameMaxWidth = pageWidth - nameX - 15;

    const companyNameText = doc.splitTextToSize(
      (company?.name || "COMPANY NAME").toUpperCase(),
      nameMaxWidth,
    );

    doc.text(companyNameText, nameX, nameY);

    // Calculate Y after company name
    let currentY = nameY + companyNameText.length * 6;

    // ================= COMPANY DETAILS =================
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);

    const detailX = nameX;
    const detailMaxWidth = nameMaxWidth;

    // Address
    const addressText = doc.splitTextToSize(
      company?.address || "Company Address",
      detailMaxWidth,
    );
    doc.text(addressText, detailX, currentY);
    currentY += addressText.length * 5;

    // Email
    const emailText = doc.splitTextToSize(
      `Email: ${company?.email || "-"}`,
      detailMaxWidth,
    );
    doc.text(emailText, detailX, currentY);
    currentY += emailText.length * 5;

    // Phone
    const phoneText = doc.splitTextToSize(
      `Phone: ${company?.contact || "-"}`,
      detailMaxWidth,
    );
    doc.text(phoneText, detailX, currentY);
    currentY += phoneText.length * 5;

    // Divider
    doc.line(10, currentY + 4, pageWidth - 10, currentY + 4);

    // ================= REPORT TITLE =================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Collection Report", pageWidth / 2, currentY + 14, {
      align: "center",
    });

    // ================= TABLE =================
    const tableColumn = [
      "#",
      "Customer Name",
      "Loan Number",
      "EMI Number",
      "EMI Amount",
      "Principal",
      "Interest",
      "Outstanding",
      "Due Date",
      "Payment Date",
      "Status",
    ];

    const tableRows = currentData.map((c, index) => [
      startIndex + index + 1,
      c.customerName,
      c.loanNumber,
      c.emiNumber,
      `Rs. ${c.emiAmount.toLocaleString("en-GB")}`,
      `Rs. ${c.principalComponent.toLocaleString("en-GB")}`,
      `Rs. ${c.interestComponent.toLocaleString("en-GB")}`,
      `Rs. ${c.outstanding.toLocaleString("en-GB")}`,
      new Date(c.dueDate).toLocaleDateString("en-GB"),
      c.paymentDate ? new Date(c.paymentDate).toLocaleDateString("en-GB") : "-",
      c.status.toUpperCase(),
    ]);

    autoTable(doc, {
      startY: currentY + 20,
      head: [tableColumn],
      body: tableRows,

      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 3,
        halign: "center",
      },
      headStyles: {
        fontStyle: "bold",
        fontSize: 7,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },

      didDrawPage: function () {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        doc.setDrawColor(0);
        doc.setLineWidth(0.2);

        // Page border on EVERY page
        doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
      },
    });

    // ================= FOOTER =================
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
      doc.text("Generated by Loan System", 14, pageHeight - 10);
    }

    doc.save("Collection_Report.pdf");
  };

  // 🔹 Excel Export
  const handleDownloadExcel = () => {
    const excelData = currentData.map((c, index) => ({
      "#": startIndex + index + 1,
      "Customer Name": c.customerName,
      "Loan Number": c.loanNumber,
      "EMI Number": c.emiNumber,
      "EMI Amount": c.emiAmount,
      Principal: c.principalComponent,
      Interest: c.interestComponent,
      Outstanding: c.outstanding,
      "Due Date": new Date(c.dueDate).toLocaleDateString("en-IN"),
      "Payment Date": c.paymentDate
        ? new Date(c.paymentDate).toLocaleDateString("en-IN")
        : "-",
      Status: c.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Collection Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "Collection_Report.xlsx");
  };

  return (
    <div className="CollectionReport-wrapper">
      <h1 className="CollectionReport-title">📋 Collection Report</h1>

      <div className="CollectionReport-grid">
        <div className="CollectionReport-formGroup">
          <label>Select Employee</label>
          <select
            value={selectedEmployee?.id || ""}
            onChange={(e) => {
              const val = e.target.value;
              const employee = employeesList.find(
                (em) => em.id === Number(val),
              );
              setSelectedEmployee(employee || null);
            }}
          >
            <option value="">Select Employee</option>
            {employeesList.map((e) => (
              <option key={e.id} value={e.id}>
                {e.fullName}
              </option>
            ))}
          </select>
        </div>
        <div
          style={{ display: "flex", flexDirection: "column" }}
          className="CollectionReport-formGroup"
        >
          <label>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="CollectionReport-search"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="payment_requested">Requested</option>
            {/* <option value="pending">Pending</option>
            <option value="partial">Partial</option> */}
          </select>
        </div>
        <div className="CollectionReport-formGroup">
          <label>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="CollectionReport-formGroup">
          <label>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        {/* <select
            className="collectionreport-loantype-select"
            value={loanType}
            onChange={(e) => {
              setLoanType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Loan Types</option>
            <option value="Fixed">Fixed</option>
            <option value="Personal">Personal</option>
          </select> */}
      </div>

      {/* Controls */}
      <div className="CollectionReport-controls">
        <label>
          Show{" "}
          <select onChange={handleItemsPerPageChange} value={itemsPerPage}>
            {ITEMS_PER_PAGE_OPTIONS.map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
          entries
        </label>

        {currentData.length > 0 && (
          <div
            className=""
            style={{ display: "flex", gap: "5px", marginTop: "10px" }}
          >
            <button
              className="collection-report-filter-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
              onClick={handleDownloadExcel}
            >
              <FileTextIcon size={18} /> Excel
            </button>
            <button
              className="collection-report-filter-btn"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
              onClick={handleDownloadPDF}
            >
              <Download size={18} /> PDF
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="CollectionReport-table-container">
        {loading ? (
          <p className="CollectionReport-loading">⏳ Loading data...</p>
        ) : (
          <table className="CollectionReport-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer Name</th>
                <th>Loan Number</th>
                <th>EMI Number</th>
                <th>EMI Amount</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Outstanding</th>
                <th>Due Date</th>
                <th>Payment Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan="11" className="CollectionReport-empty">
                    ✅ No records found
                  </td>
                </tr>
              ) : (
                currentData.map((c, idx) => (
                  <tr key={c.id}>
                    <td>{startIndex + idx + 1}</td>
                    <td>{c.customerName || "N/A"}</td>
                    <td>{c.loanNumber || "N/A"}</td>
                    <td>{c.emiNumber || "N/A"}</td>
                    <td>{`₹${c.emiAmount.toLocaleString()}` || "N/A"}</td>
                    <td>
                      {`₹${c.principalComponent.toLocaleString()}` || "N/A"}
                    </td>
                    <td>
                      {`₹${c.interestComponent.toLocaleString()}` || "N/A"}
                    </td>
                    <td>{`₹${c.outstanding.toLocaleString()}` || "N/A"}</td>
                    <td>
                      {new Date(c.dueDate).toLocaleDateString("en-GB") || "N/A"}
                    </td>
                    <td>
                      {c.paymentDate
                        ? new Date(c.paymentDate).toLocaleDateString("en-GB")
                        : "N/A"}
                    </td>
                    <td>
                      <span
                        className={`CollectionReport-status ${c.status?.toLowerCase()}`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td>
                      {c.status?.toLowerCase() === "payment_requested" ? (
                        <button
                          type="button"
                          className="CollectionReport-verify-btn"
                          onClick={() => handleOpenVerifyModal(c)}
                        >
                          Verify
                        </button>
                      ) : (
                        <span className="CollectionReport-no-action">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 🔹 Verify Collection Modal */}
      {verifyModalOpen && selectedCollection && (
        <div
          className="cr-modal-overlay"
          onClick={() => !verifying && setVerifyModalOpen(false)}
        >
          <div
            className="cr-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cr-modal-header">
              <h3>Verify EMI Payment</h3>
              <button
                type="button"
                className="cr-modal-close"
                disabled={verifying}
                onClick={() => setVerifyModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="cr-modal-body">
              {/* Loan & Customer Summary */}
              <div className="cr-detail-row">
                <span>Customer Name:</span>
                <strong>{selectedCollection.customerName || "N/A"}</strong>
              </div>
              <div className="cr-detail-row">
                <span>Loan Number:</span>
                <strong>{selectedCollection.loanNumber || "N/A"}</strong>
              </div>
              <div className="cr-detail-row">
                <span>EMI Number / ID:</span>
                <strong>
                  {selectedCollection.emiNumber ||
                    selectedCollection.id ||
                    "N/A"}
                </strong>
              </div>
              <div className="cr-detail-row">
                <span>Amount to Pay:</span>
                <strong className="cr-amount-highlight">
                  ₹
                  {Number(selectedCollection.emiAmount || 0).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>

              {/* Form Inputs for Payload */}
              <div className="cr-form-field">
                <label htmlFor="paymentModeSelect">Payment Mode *</label>
                <select
                  id="paymentModeSelect"
                  className="cr-input"
                  value={paymentMode}
                  disabled={verifying}
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI / QR</option>
                  <option value="NET_BANKING">Net Banking / IMPS</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CARD">Debit / Credit Card</option>
                </select>
              </div>

              <div className="cr-form-field">
                <label htmlFor="paymentDateInput">Payment Date *</label>
                <input
                  id="paymentDateInput"
                  type="date"
                  className="cr-input"
                  value={paymentDate}
                  disabled={verifying}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>
            </div>

            <div className="cr-modal-footer">
              <button
                type="button"
                className="cr-btn-cancel"
                disabled={verifying}
                onClick={() => setVerifyModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cr-btn-confirm"
                disabled={verifying}
                onClick={handleConfirmVerify}
              >
                {verifying ? "Processing..." : "Confirm & Pay EMI"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && (
        <div className="CollectionReport-pagination">
          <button
            className="pagination-btn"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            ⬅ Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next ➡
          </button>
        </div>
      )}
    </div>
  );
};

export default CollectionReport;
